// eslint-disable-next-line no-unused-vars
import React, {useState, useRef, useEffect} from 'react';
import styled from 'styled-components';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../../hooks/useAuth';
import {getActiveServers} from '../../api/axios';
import {
  FaHome,
  FaUserPlus,
  FaDownload,
  FaTrophy,
  FaBook,
  FaBars,
  FaTimes,
  FaVoteYea,
  FaGift,
  FaShoppingCart,
  FaCoins,
  FaChevronDown,
  FaInfoCircle,
  FaSignInAlt,
  FaUserShield,
  FaCog,
  FaSignOutAlt,
} from 'react-icons/fa';

// =============================
//       Navbar Component
// =============================
const CustomNavbarv2 = () => {
  const {t, i18n} = useTranslation();
  const {user, logout} = useAuth();

  // Mobile Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const sideDrawerRef = useRef(null);

  // Language Dropdown (Desktop)
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);

  // User Dropdown (Desktop)
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);

  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
  const shopDropdownRef = useRef(null);

  const [infoDropdownOpen, setInfoDropdownOpen] = useState(false);
  const infoDropdownRef = useRef(null);

  const [voteRewardsDropdownOpen, setVoteRewardsDropdownOpen] = useState(false);
  const voteRewardsDropdownRef = useRef(null);

  const [active, setActive] = useState({pvp: false, saison: false});

  useEffect(() => {
    let mounted = true;
    getActiveServers()
      .then(res => {
        if (!mounted) return;
        const list = Array.isArray(res?.data?.servers) ? res.data.servers : [];
        const keys = list.map(s => s.friendly_key);
        setActive({
          pvp: keys.includes('pvp'),
          saison: keys.includes('saison'),
        });
      })
      .catch(() => setActive({pvp: false, saison: false}));
    return () => {
      mounted = false;
    };
  }, [active]);

  // Klick außerhalb -> Dropdown schließen
  useEffect(() => {
    const handleClickOutside = e => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(e.target)) {
        setShopDropdownOpen(false);
      }
      if (infoDropdownRef.current && !infoDropdownRef.current.contains(e.target)) {
        setInfoDropdownOpen(false);
      }
      if (voteRewardsDropdownRef.current && !voteRewardsDropdownRef.current.contains(e.target)) {
        setVoteRewardsDropdownOpen(false);
      }
      if (sideDrawerRef.current && !sideDrawerRef.current.contains(e.target)) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSettingsClick = e => {
    e.preventDefault();
    setDrawerOpen(false);
  };

  // Toggle für Shop-Dropdown
  const toggleShopDropdown = () => {
    setShopDropdownOpen(prev => !prev);
    // Schließt die anderen Dropdowns, falls sie offen sind:
    if (infoDropdownOpen) setInfoDropdownOpen(false);
    if (voteRewardsDropdownOpen) setVoteRewardsDropdownOpen(false);
  };

  // Toggle für Info-Dropdown
  const toggleInfoDropdown = () => {
    setInfoDropdownOpen(prev => !prev);
    // Schließt die anderen Dropdowns, falls sie offen sind:
    if (shopDropdownOpen) setShopDropdownOpen(false);
    if (voteRewardsDropdownOpen) setVoteRewardsDropdownOpen(false);
  };

  // Toggle für Vote & Rewards-Dropdown
  const toggleVoteRewardsDropdown = () => {
    setVoteRewardsDropdownOpen(prev => !prev);
    // Schließt die anderen Dropdowns, falls sie offen sind:
    if (shopDropdownOpen) setShopDropdownOpen(false);
    if (infoDropdownOpen) setInfoDropdownOpen(false);
  };

  // Sprachdaten
  const languages = [
    {code: 'en', label: 'English', flag: '/assets/flags/en_us.svg'},
    {code: 'de', label: 'Deutsch', flag: '/assets/flags/de.svg'},
    {code: 'pl', label: 'Polski', flag: '/assets/flags/pl.svg'},
    {code: 'fr', label: 'Français', flag: '/assets/flags/fr.svg'},
    {code: 'ar', label: 'العربية', flag: '/assets/flags/ar.svg'},
    {code: 'tr', label: 'Türkçe', flag: '/assets/flags/tr.svg'},
    {code: 'ru', label: 'Русский', flag: '/assets/flags/ru.svg'},
  ];
  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];
  const changeLanguage = code => {
    i18n.changeLanguage(code);
    setLangDropdownOpen(false);
  };

  const isLoggedIn = !!user;

  return (
    <NavbarWrapper>
      {/* Haupt-Navbar-Zeile */}
      <NavbarContent>
        {/* ===================== Desktop Links ===================== */}
        <DesktopNav>
          {/* Eingeloggt vs nicht eingeloggt */}
          {isLoggedIn ? (
            <>
              <LogoLink to="/">
                <img src="/assets/logo/logo.png" alt="Logo" width="130" height="30" />
              </LogoLink>
              <NavItemLink to="/dashboard">
                <FaHome /> {t('navbar.dashboard')}
              </NavItemLink>

              <DropdownWrapper ref={voteRewardsDropdownRef}>
                <NavItemLink disabled onClick={toggleVoteRewardsDropdown}>
                  <FaGift /> {t('navbar.rewardvote')} <FaChevronDown />
                </NavItemLink>
                {voteRewardsDropdownOpen && (
                  <DropdownMenu>
                    <DropdownItemNav to={'/vote'} onClick={() => setVoteRewardsDropdownOpen(false)}>
                      <FaVoteYea /> {t('navbar.vote')}
                    </DropdownItemNav>
                    <DropdownItemNav to={'/reward'} onClick={() => setVoteRewardsDropdownOpen(false)}>
                      <FaGift /> {t('navbar.reward')}
                    </DropdownItemNav>
                  </DropdownMenu>
                )}
              </DropdownWrapper>

              <DropdownWrapper ref={infoDropdownRef}>
                <NavItemLink disabled onClick={toggleInfoDropdown}>
                  <FaInfoCircle /> {t('navbar.info')} <FaChevronDown />
                </NavItemLink>
                {infoDropdownOpen && (
                  <DropdownMenu>
                    <DropdownItemNav to={'/ranking'} onClick={() => setInfoDropdownOpen(false)}>
                      <FaTrophy /> {t('navbar.ranking')}
                    </DropdownItemNav>
                    <DropdownItemNav to={'/wiki'} onClick={() => setInfoDropdownOpen(false)}>
                      <FaBook /> {t('navbar.wiki')}
                    </DropdownItemNav>
                  </DropdownMenu>
                )}
              </DropdownWrapper>
              <DropdownWrapper ref={shopDropdownRef}>
                <NavItemLink disabled onClick={toggleShopDropdown}>
                  <FaShoppingCart /> {t('navbar.shop')} <FaChevronDown />
                </NavItemLink>
                {shopDropdownOpen && (
                  <DropdownMenu>
                    <DropdownItemNav to={'/shop'} onClick={() => setShopDropdownOpen(false)}>
                      <FaShoppingCart /> {t('navbar.shop')}
                    </DropdownItemNav>
                    <DropdownItemNav to={'/purchase'} onClick={() => setShopDropdownOpen(false)}>
                      <FaCoins /> {t('navbar.purchase')}
                    </DropdownItemNav>
                  </DropdownMenu>
                )}
              </DropdownWrapper>
            </>
          ) : (
            <>
              <LogoLink to="/">
                <img src="/assets/logo/logo.png" alt="Logo" width="130" height="30" />
              </LogoLink>
              <NavItemLink to="/download">
                <FaDownload /> {t('navbar.download')}
              </NavItemLink>
              <NavItemLink to="/ranking">
                <FaTrophy /> {t('navbar.ranking')}
              </NavItemLink>
              <NavItemLink to="/wiki">
                <FaBook /> {t('navbar.wiki')}
              </NavItemLink>
            </>
          )}
        </DesktopNav>

        {/* ===================== Mobile Header Row ===================== */}
        <MobileHeaderRow>
          <MobileLeft>
            <LogoLink to="/" onClick={() => setDrawerOpen(false)}>
              <img src="/assets/logo/logo.png" alt="Logo" width="130" height="30" />
            </LogoLink>
          </MobileLeft>

          <MobileCenter>
            {drawerOpen ? (
              <FaTimes style={{fontSize: '24px', cursor: 'pointer'}} onClick={() => setDrawerOpen(false)} />
            ) : (
              <FaBars style={{fontSize: '24px', cursor: 'pointer'}} onClick={() => setDrawerOpen(true)} />
            )}
          </MobileCenter>

          <MobileRight>
            <DropdownWrapper ref={langDropdownRef}>
              <LangNavButton onClick={() => setLangDropdownOpen(!langDropdownOpen)}>
                <img src={currentLang.flag} alt={`${currentLang.label} Flag`} width="30" height="30" />
              </LangNavButton>
              {langDropdownOpen && (
                <DropdownMenu>
                  {languages.map(lang => (
                    <DropdownItem
                      key={lang.code}
                      onTouchStart={() => {
                        changeLanguage(lang.code);
                        setDrawerOpen(false);
                      }}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setDrawerOpen(false);
                      }}
                    >
                      <img src={lang.flag} alt={`${lang.label} Flag`} width="25" height="25" />
                      {lang.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              )}
            </DropdownWrapper>
          </MobileRight>
        </MobileHeaderRow>

        {/* ===================== Desktop Rechts ===================== */}
        <DesktopNav>
          {!isLoggedIn ? (
            <>
              <NavItemLink to="/register">
                <FaUserPlus /> {t('navbar.register')}
              </NavItemLink>
              <NavItemLink to="/login">
                <FaSignInAlt /> {t('navbar.signIn')}
              </NavItemLink>
              <DropdownWrapper ref={langDropdownRef}>
                <LangNavButton onClick={() => setLangDropdownOpen(!langDropdownOpen)}>
                  <img src={currentLang.flag} alt={`${currentLang.label} Flag`} width="30" height="30" />
                </LangNavButton>
                {langDropdownOpen && (
                  <DropdownMenuFlag>
                    {languages.map(lang => (
                      <DropdownItem key={lang.code} onClick={() => changeLanguage(lang.code)}>
                        <img src={lang.flag} alt={`${lang.label} Flag`} width="25" height="25" />
                        {lang.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenuFlag>
                )}
              </DropdownWrapper>
            </>
          ) : (
            <>
              <PointsDisplay>
                {t('loginContainer.arcanium')}: {user?.cash_points || 0} <br />
                {'Arc '}: {'Hybrid: ' + (user?.arc_hybrid || 0)} {active.pvp && <> {' | PvP: ' + (user?.arc_pvp || 0)}</>} {active.saison && <> {' | Saison: ' + (user?.arc_saison || 0)}</>}<br />
                {t('loginContainer.votePoints')}: {user?.vote_points || 0}
              </PointsDisplay>

              <DropdownWrapper ref={userDropdownRef}>
                <NavButton onClick={() => setUserDropdownOpen(!userDropdownOpen)}>
                  {user.username} <FaChevronDown />
                </NavButton>
                {userDropdownOpen && (
                  <DropdownMenuFlag>
                    {user.permission === 3 && (
                      <DropdownItemNav to={'/admin'} onClick={() => setUserDropdownOpen(false)}>
                        <FaUserShield /> {t('loginContainer.adminPanel')}
                      </DropdownItemNav>
                    )}
                    <DropdownItemNav to={'/settings'} onClick={() => setUserDropdownOpen(false)}>
                      <FaCog /> {t('loginContainer.settings')}
                    </DropdownItemNav>
                    <DropdownItemNav onClick={logout}>
                      <FaSignOutAlt /> {t('loginContainer.logout')}
                    </DropdownItemNav>
                  </DropdownMenuFlag>
                )}
              </DropdownWrapper>

              <DropdownWrapper ref={langDropdownRef}>
                <LangNavButton onClick={() => setLangDropdownOpen(!langDropdownOpen)}>
                  <img src={currentLang.flag} alt={`${currentLang.label} Flag`} width="30" height="30" />
                </LangNavButton>
                {langDropdownOpen && (
                  <DropdownMenuFlag>
                    {languages.map(lang => (
                      <DropdownItem key={lang.code} onClick={() => changeLanguage(lang.code)}>
                        <img src={lang.flag} alt={`${lang.label} Flag`} width="25" height="25" />
                        {lang.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenuFlag>
                )}
              </DropdownWrapper>
            </>
          )}
        </DesktopNav>
      </NavbarContent>

      {/* ========== SEITENMENÜ (SIDE DRAWER) für MOBILE ========== */}
      <SideDrawer ref={sideDrawerRef} open={drawerOpen}>
        {/* Restlicher Inhalt im SideDrawer */}
        {!isLoggedIn && (
          <>
            <DrawerSection>
              <NavItemLink to="/download" onClick={() => setDrawerOpen(false)}>
                <FaDownload /> {t('navbar.download')}
              </NavItemLink>
              <NavItemLink to="/ranking" onClick={() => setDrawerOpen(false)}>
                <FaTrophy /> {t('navbar.ranking')}
              </NavItemLink>
              <NavItemLink to="/wiki" onClick={() => setDrawerOpen(false)}>
                <FaBook /> {t('navbar.wiki')}
              </NavItemLink>
            </DrawerSection>
            <DrawerSection>
              <NavItemLink to="/register" onClick={() => setDrawerOpen(false)}>
                <FaUserPlus /> {t('navbar.register')}
              </NavItemLink>
              <NavItemLink to="/login" onClick={() => setDrawerOpen(false)}>
                <FaSignInAlt /> {t('navbar.signIn')}
              </NavItemLink>
            </DrawerSection>
          </>
        )}

        {isLoggedIn && (
          <>
            <DrawerSection>
              <NavButton disabled style={{cursor: 'default'}}>
                {user.username}
              </NavButton>
              <div style={{fontSize: '14px'}}>
                {t('loginContainer.arcanium')}: {user?.cash_points || 0} <br />
                {'Arc Hybrid: ' + (user?.arc_hybrid || 0)}<br /> {active.pvp && <> {' Arc PvP: ' + (user?.arc_pvp || 0)}</>}<br /> {active.saison && <> {' Arc Saison: ' + (user?.arc_saison || 0)}</>}<br />
                {t('loginContainer.votePoints')}: {user?.vote_points || 0}
              </div>
            </DrawerSection>
            <DrawerSection>
              <NavItemLink to="/dashboard" onClick={() => setDrawerOpen(false)}>
                <FaHome /> {t('navbar.dashboard')}
              </NavItemLink>
              <NavItemLink to="/ranking" onClick={() => setDrawerOpen(false)}>
                <FaTrophy /> {t('navbar.ranking')}
              </NavItemLink>
              <NavItemLink to="/wiki" onClick={() => setDrawerOpen(false)}>
                <FaBook /> {t('navbar.wiki')}
              </NavItemLink>
              <NavItemLink to="/vote" onClick={() => setDrawerOpen(false)}>
                <FaVoteYea /> {t('navbar.vote')}
              </NavItemLink>
              <NavItemLink to="/reward" onClick={() => setDrawerOpen(false)}>
                <FaGift /> {t('navbar.reward')}
              </NavItemLink>
              <NavItemLink to="/shop" onClick={() => setDrawerOpen(false)}>
                <FaShoppingCart /> {t('navbar.shop')}
              </NavItemLink>
              <NavItemLink to="/purchase" onClick={() => setDrawerOpen(false)}>
                <FaCoins /> {t('navbar.purchase')}
              </NavItemLink>
            </DrawerSection>
            <DrawerSection ref={sideDrawerRef}>
              {user.permission === 3 && (
                <NavItemLinkButton onClick={handleSettingsClick} to={'/admin'}>
                  <FaUserShield /> {t('loginContainer.adminPanel')}
                </NavItemLinkButton>
              )}
              <NavItemLinkButton onClick={handleSettingsClick} to={'/settings'}>
                <FaCog /> {t('loginContainer.settings')}
              </NavItemLinkButton>
              <NavItemLinkButton
                onClick={() => {
                  logout();
                  setDrawerOpen(false);
                }}
              >
                <FaSignOutAlt /> {t('loginContainer.logout')}
              </NavItemLinkButton>
            </DrawerSection>
          </>
        )}
      </SideDrawer>
    </NavbarWrapper>
  );
};

export default CustomNavbarv2;

// =============================
//     Styled Components
// =============================

// Oberster Wrapper mit Hintergrundbild
const NavbarWrapper = styled.header`
  width: 100%;
  background-image: url('/assets/ui/navbar.png');
  background-repeat: no-repeat;
  background-size: 100% 100%;
  color: #fff;
  position: fixed;
  z-index: 999;
`;

// Maximalbreite und horizontales Padding für den Inhalt
const NavbarContent = styled.div`
  margin: 0 auto;
  padding: 0 20px;

  /* Höhe der Navbar, Ausrichtung */
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/* ---------------------------------
   Desktop-Bereich (ab 992px)
-----------------------------------*/
const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  /* Nur sichtbar ab 992px */
  @media (max-width: 991px) {
    display: none;
  }
`;

/* ---------------------------------
   Mobile-Bereich (bis 991px)
-----------------------------------*/
/**
 * MobileHeaderRow:
 * In einer Zeile:
 * 1) Logo (links)
 * 2) Hamburger (Mitte)
 * 3) Sprache (rechts)
 */
const MobileHeaderRow = styled.div`
  display: none;

  @media (max-width: 991px) {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
  }
`;

// Container für die drei Zonen: links, mitte, rechts
const MobileLeft = styled.div`
  /* Logo links */
`;
const MobileCenter = styled.div`
  /* Hamburger in der Mitte */
  display: flex;
  justify-content: center;
  flex: 1; /* Nimmt die mittlere Breite ein */
`;
const MobileRight = styled.div`
  /* Sprache rechts */
`;

// Klickbarer Logo-Link
const LogoLink = styled(Link)`
  img {
    vertical-align: middle;
    transition: all 0.3s;
  }
  &:hover img {
    border-bottom: 2px solid #ffcc00;
  }
`;

// Normaler Link-Stil
const NavItemLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: bold;
  position: relative;
  padding-bottom: 2px;
  transition: color 0.3s;

  &:hover {
    color: #ffcc00;
  }

  &:hover::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    border-bottom: 2px solid #ffcc00;
  }
  margin-right: 10px; /* kleiner Abstand */
`;

// Normaler Link-Stil
const NavItemLinkButton = styled(Link)`
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  position: relative;
  padding-bottom: 2px;
  transition: color 0.3s;
  margin-right: 10px;

  &:hover {
    color: #ffcc00;
  }
  &:hover::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    border-bottom: 2px solid #ffcc00;
  }
`;

const DropdownItemNav = styled(Link)`
  background: none;
  border: none;
  height: 40px;
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;

  &:hover {
    background: #333;
  }
`;

// Button-Stil (z. B. für Username-Klick)
const NavButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  position: relative;
  padding-bottom: 2px;
  transition: color 0.3s;
  margin-right: 10px;

  &:hover {
    color: #ffcc00;
  }
  &:hover::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    border-bottom: 2px solid #ffcc00;
  }
`;

const LangNavButton = styled.button`
  && {
    width: 70px !important;
    padding: 2px 5px;
  }
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #ffcc00;
  }

  img {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

// Bereich für Arcanium- und Vote-Punkte
const PointsDisplay = styled.div`
  text-align: right;
  line-height: 1.2;
  margin-right: 15px;
`;

// Dropdown-Wrapper
const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

// Dropdown-Menü

const DropdownMenu = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  background-image: url(/assets/ui/container-main-bg.png);
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  border: 1px solid #444;
  border-radius: 4px;
  z-index: 9999;
  min-width: 200px;
  overflow: hidden;
`;

const DropdownMenuFlag = styled(DropdownMenu)`
  top: 60px;
  right: 0;
`;

const DropdownItem = styled.button`
  background: none;
  border: none;
  height: 40px;
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;

  &:hover {
    background: #333;
  }

  img {
    width: 25px;
    height: 25px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

// =============================
//       Mobile SideDrawer (angepasst)
// =============================
const SideDrawer = styled.div`
  background-image: url(/assets/ui/container-main-bg.png);
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  position: fixed;
  top: 70px; /* Setze das auf die Höhe deiner Navbar */
  left: 0;
  height: calc(100vh - 70px); /* Damit der Drawer darunter startet */
  width: 50%;
  color: #fff;
  z-index: 1000;
  transform: ${({open}) => (open ? 'translateX(0)' : 'translateX(-100%)')};
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  overflow-y: auto;
  @media (min-width: 992px) {
    display: none;
  }
`;

// Jede Section im Drawer (für vertikale Anordnung)
const DrawerSection = styled.div`
  padding: 20px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;
